import { Box, Button, TextField, Typography } from "@mui/material";
import React from "react";
import { useTranslate } from "react-admin";
import { useForm } from "react-hook-form";

import { UserType } from "../generated/graphql";
import { PASSWORD_MIN_LENGTH } from "../lib/validation";
import { space } from "../theme/designTokens";
import { RegistrationCandidate } from "../types/registration";
import { AuthFormHeader } from "./AuthFormHeader";

export type RegistrationFormData = {
  name: string;
  password: string;
  confirmPassword: string;
};

type RegistrationFormProps = {
  loading: boolean;
  candidate: RegistrationCandidate;
  onSubmit: (data: RegistrationFormData) => void;
};

export const RegistrationForm: React.FC<RegistrationFormProps> = ({ loading, candidate, onSubmit }) => {
  const translate = useTranslate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<RegistrationFormData>({
    mode: "onChange",
    defaultValues: {
      name: "",
      password: "",
      confirmPassword: "",
    },
  });

  const password = watch("password");

  const isStaff = candidate.kind === UserType.CustomerService;

  return (
    <>
      <AuthFormHeader
        title={translate("registration.title")}
        description={translate(isStaff ? "registration.staffDescription" : "registration.description")}
      />

      {candidate.kind === UserType.CustomerService && (
        <Typography variant="body2" color="text.secondary" data-testid="registration-invited-roles">
          {translate("registration.invitedRoles", {
            roles: candidate.roles.map(role => translate(`staffRoles.${role}`)).join(", "),
          })}
        </Typography>
      )}

      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {candidate.kind === UserType.Manager ? (
          // These fields are locked and not registered. The explicit name attributes are
          // kept because cross-app E2E selects input[name=...].
          <TextField
            fullWidth
            label={translate("registration.name")}
            margin="normal"
            disabled
            name="name"
            value={candidate.managerName}
            helperText={translate("registration.prefilledFromInvite")}
          />
        ) : (
          <TextField
            fullWidth
            label={translate("registration.name")}
            margin="normal"
            required
            autoComplete="name"
            disabled={loading}
            error={!!errors.name}
            helperText={errors.name?.message}
            {...register("name", {
              required: translate("registration.nameRequired"),
              validate: value => value.trim() !== "" || translate("registration.nameRequired"),
            })}
          />
        )}

        <TextField
          fullWidth
          label={translate("registration.password")}
          type="password"
          margin="normal"
          required
          disabled={loading}
          error={!!errors.password}
          helperText={errors.password?.message}
          {...register("password", {
            required: translate("registration.passwordRequired"),
            minLength: {
              value: PASSWORD_MIN_LENGTH,
              message: translate("registration.passwordMinLength", { minLength: PASSWORD_MIN_LENGTH }),
            },
          })}
        />

        <TextField
          fullWidth
          label={translate("registration.confirmPassword")}
          type="password"
          margin="normal"
          required
          disabled={loading}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
          {...register("confirmPassword", {
            required: translate("registration.confirmPasswordRequired"),
            validate: value => value === password || translate("registration.passwordsDoNotMatch"),
          })}
        />

        {candidate.kind === UserType.Manager && (
          <TextField
            fullWidth
            label={translate("registration.organizationName")}
            margin="normal"
            disabled
            name="organizationName"
            value={candidate.organizationName}
            helperText={translate("registration.prefilledFromInvite")}
          />
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disabled={loading || !isValid}
          sx={{ marginTop: `${space.xl}px` }}
        >
          {loading ? translate("registration.creatingAccount") : translate("registration.completeRegistration")}
        </Button>
      </Box>
    </>
  );
};
